import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, ShieldCheck } from 'lucide-react';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-navy-950 text-white">
      {/* Upper Footer */}
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand Column */}
          <div className="space-y-6">
            <Link to="/" className="inline-block bg-white p-2.5 rounded-none">
              <img
                src="https://assets.auxodev.dk/assets/c02e6411-90cc-481d-8aeb-e9d9691b4c94/de-logo-vek2-hvid2.png"
                alt="D.E. TekniQ K/S Logo"
                className="h-24 w-auto"
              />
            </Link>
            <p className="text-sm text-navy-200 leading-relaxed">
              Din lokale el- og energispecialist i Midtjylland. Vi leverer professionelle løsninger inden for varmepumper, el-installation, sikring og netværk.
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/detekniq/"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-none bg-navy-900 p-2.5 text-white transition-colors hover:bg-energy-600"
                aria-label="Følg os på Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="/_assets/certifikat-248-2-2026-2029-6289.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-none bg-navy-900 px-3 py-1.5 text-xs font-semibold text-energy-400 hover:bg-navy-800 hover:text-energy-300 transition-colors"
                title="Åbn ISO 9001 Certifikat (PDF)"
              >
                <ShieldCheck className="h-4 w-4" />
                <span>ISO 9001 Certifikat</span>
              </a>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-energy-400">Ydelser</h3>
            <ul className="mt-6 space-y-3.5 text-sm">
              <li>
                <Link to="/varmepumper/daikin-luft-til-vand" className="text-navy-200 hover:text-white transition-colors">Varmepumper</Link>
              </li>
              <li>
                <Link to="/el-installation/traditionelt-el-arbejde" className="text-navy-200 hover:text-white transition-colors">El-installationer</Link>
              </li>
              <li>
                <Link to="/sikring-og-netvaerk/alarmsystemer-og-overvaagning" className="text-navy-200 hover:text-white transition-colors">Sikring & Overvågning</Link>
              </li>
              <li>
                <Link to="/sikring-og-netvaerk/teledata-og-netvaerk" className="text-navy-200 hover:text-white transition-colors">Teledata & Netværk</Link>
              </li>
              <li>
                <Link to="/varmepumper/daikin-luft-til-vand" className="text-navy-200 hover:text-white transition-colors">Daikin Varmepumper</Link>
              </li>
            </ul>
          </div>

          {/* Opening Hours Column */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-energy-400">Åbningstider</h3>
            <ul className="mt-6 space-y-4 text-sm text-navy-200">
              <li className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-energy-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-white">Mandag – Torsdag</p>
                  <p className="text-xs text-navy-300 mt-0.5">07:30 – 16:00</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-energy-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-white">Fredag</p>
                  <p className="text-xs text-navy-300 mt-0.5">07:30 – 13:00</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-navy-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-navy-300">Lørdag – Søndag</p>
                  <p className="text-xs text-navy-400 mt-0.5">Lukket</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-energy-400">Kontakt os</h3>
            <ul className="mt-6 space-y-4 text-sm text-navy-200">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-energy-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-white">D.E. TekniQ K/S</p>
                  <p className="text-xs text-navy-300 mt-0.5">Nørrevangsvej 5, 8620 Kjellerup</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-energy-500 shrink-0" />
                <div>
                  <p className="font-semibold text-white">Telefon</p>
                  <a href="tel:+4586667700" className="text-xs text-navy-300 hover:text-energy-400 transition-colors whitespace-nowrap">
                    +45 86 66 77 00
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-energy-500 shrink-0" />
                <div>
                  <p className="font-semibold text-white">E-mail</p>
                  <a href="mailto:post@detekniq.dk" className="text-xs text-navy-300 hover:text-energy-400 transition-colors whitespace-nowrap">
                    post@detekniq.dk
                  </a>
                </div>
              </li>
              <li className="border-t border-navy-900 pt-4 text-xs text-navy-400">
                CVR nr.: 34211361
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Lower Footer */}
      <div className="border-t border-navy-900 bg-navy-950 py-6 text-xs text-navy-400">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p>© {currentYear} D.E. TekniQ K/S. Alle rettigheder forbeholdes.</p>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs">
            <a
              href="/_assets/certifikat-248-2-2026-2029-6289.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-navy-300 hover:text-energy-400 transition-colors underline-offset-4 hover:underline"
            >
              ISO 9001 Certifikat (PDF)
            </a>
            <span className="text-navy-700 hidden sm:inline">•</span>
            <a
              href="/_assets/vsme-esg-rapport-2025-2.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-navy-300 hover:text-energy-400 transition-colors underline-offset-4 hover:underline"
            >
              VSME ESG Rapport 2025 (PDF)
            </a>
          </div>
          <p className="text-[10px] uppercase tracking-wider text-navy-500">Autoriseret El- & Alarminstallatør · VE-Godkendt</p>
        </div>
      </div>
    </footer>
  );
}
